import {isEditableElement} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {GlobalCommands} from '@github-ui/ui-commands'
import type React from 'react'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef} from 'react'

import {useCopyRawBlobContents} from '../hooks/use-copy-raw-blob-contents'

interface SelectAllShortcutProps {
  containerRef: React.RefObject<HTMLElement | null> | undefined
  shouldNotOverrideCopy?: boolean
}

/**
 * This component intercepts the select-all shortcut and selects the contents
 * of the given container instead of the contents of the entire document.
 *
 * In addition, it also intercepts the copy shortcut. If the current selection
 * was created through select-all, it will copy the raw blob contents instead
 * of the current selection. This is because when the code blob is virtualized,
 * the current selection will only contain the visible lines.
 */
export default function SelectAllShortcut({containerRef, shouldNotOverrideCopy}: SelectAllShortcutProps) {
  const allSelected = useRef(false)
  const selectionChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const copyRawBlobContents = useCopyRawBlobContents()

  // If the selection changes while we are listening for this event, then that means the selection is no longer the result of Control+A.
  // Registered with `{once: true}` so it auto-removes after the first selectionchange (matching the
  // previous self-removing behavior) without the handler having to reference itself.
  const onSelectionChange = useCallback(() => {
    allSelected.current = false
  }, [])

  // Clean up the event listener on unmount
  useEffect(
    () => () => {
      if (selectionChangeTimeoutRef.current !== null) {
        clearTimeout(selectionChangeTimeoutRef.current)
      }

      document.removeEventListener('selectionchange', onSelectionChange)
    },
    [onSelectionChange],
  )

  const selectAll = useCallback(() => {
    // If the user is focused on an editable element, do not override the default,
    // and instead highlight the content of that element
    if (
      document.activeElement &&
      isEditableElement(document.activeElement) &&
      containerRef?.current !== document.activeElement
    ) {
      selectAllContentInElement(document.activeElement as HTMLElement)
      return
    }
    if (containerRef) {
      // If we're not given a container, just let the browser handle the event
      if (!containerRef.current) return

      // Restrict select all to the code blob
      selectAllContentInElement(containerRef.current)

      allSelected.current = true

      // Any changes to the selection now mean that the selection was not the result of Control+A
      if (selectionChangeTimeoutRef.current !== null) {
        clearTimeout(selectionChangeTimeoutRef.current)
      }

      selectionChangeTimeoutRef.current = setTimeout(() => {
        selectionChangeTimeoutRef.current = null
        document.addEventListener('selectionchange', onSelectionChange, {once: true})
      }, 0)
    }
  }, [containerRef, onSelectionChange])

  const copyFullFileContents = useEffectEvent((event: Event) => {
    // If the selection didn't come from select-all, let the browser handle the event
    if (!allSelected.current) return

    event.preventDefault()
    copyRawBlobContents()
  })

  // We cannot use data-hotkey for this. If we do, then regular copy events will not work properly.
  useEffect(() => {
    if (shouldNotOverrideCopy) return
    const copyEvent = (event: Event) => copyFullFileContents(event)
    window.addEventListener('copy', copyEvent)
    return () => window.removeEventListener('copy', copyEvent)
  }, [shouldNotOverrideCopy])

  const commands = useMemo(() => {
    return {
      'github:select-all': selectAll,
    }
  }, [selectAll])

  return <GlobalCommands commands={commands} />
}

function selectAllContentInElement(element: HTMLElement) {
  if (element instanceof HTMLTextAreaElement || element instanceof HTMLInputElement) {
    element.select()
    return
  }

  const range = document.createRange()
  range.selectNode(element)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}
